import { useMemo, useState } from 'react'

import {
  PopoverHorizontalPositionProps,
  PopoverVerticalPositionProps
} from '@dcxwam/dcx-wam-ui-foundations'

import type { ProductDataVariant } from '../../../../services/product/product-data'
import { getAssetImageUrl } from '../../../../utils/asset-url'
import type { CartProductEntry } from '../../common/cart-management/cartConfig'
import {
  getProductSetEntry,
  getChildProductEntries
} from '../../common/cart-management/cartConfig'
import type { ShopConfig } from '../../common/config'
import {
  pushDataLayerMiniCartSeeCartClick,
  pushDataLayerMiniCartValidateOrderClick
} from '../../common/datalayer/product'
import { getPriceInfoMiniCart } from '../../common/price-info'

interface UseMiniCartParams {
  productEntry: CartProductEntry | undefined
  miniCartTotalUnitCount: number
  variantAssetsByAddToCart: ProductDataVariant['assets']
  shopConfig: ShopConfig
  showOldPrices: boolean | undefined
  cartLocalStorageJSON?: {
    cartModifications?: Array<{
      quantity: number
      entry: {
        entryNumber: number
        entryGroupNumbers: Array<number>
        bundleName: string
        bundleQty: number
        productReference: string
      }
    }>
    cart?: {
      entries: Array<CartProductEntry>
      productSetInfo?: {
        productSets: Array<{
          code: string
          entryGroupNumber: number
          totalPrice?: { formattedValue?: string }
          totalPriceWithoutDiscounts?: { formattedValue?: string }
        }>
      }
      totalUnitCount?: string
    }
  } | null
  variants?: Array<ProductDataVariant>
  showOnlyProductSetInMiniCart?: boolean
}

export const useMiniCart = ({
  productEntry,
  miniCartTotalUnitCount,
  variantAssetsByAddToCart,
  shopConfig,
  showOldPrices,
  cartLocalStorageJSON,
  variants,
  showOnlyProductSetInMiniCart
}: UseMiniCartParams) => {
  const [activeMiniCart, setActiveMiniCart] = useState(false)

  const isProductSetResponse = Boolean(
    cartLocalStorageJSON?.cartModifications?.length
  )

  const resolvedProductEntry = useMemo(() => {
    if (isProductSetResponse && cartLocalStorageJSON?.cart) {
      return getProductSetEntry(
        cartLocalStorageJSON.cartModifications!,
        cartLocalStorageJSON.cart as Parameters<typeof getProductSetEntry>[1]
      ) as CartProductEntry
    }
    return productEntry
  }, [isProductSetResponse, cartLocalStorageJSON, productEntry])

  const childProductEntries = useMemo(() => {
    if (
      isProductSetResponse &&
      !showOnlyProductSetInMiniCart &&
      cartLocalStorageJSON?.cart
    ) {
      return getChildProductEntries(
        cartLocalStorageJSON.cartModifications!,
        cartLocalStorageJSON.cart as Parameters<
          typeof getChildProductEntries
        >[1]
      )?.filter((e): e is CartProductEntry => Boolean(e))
    }
    return undefined
  }, [isProductSetResponse, showOnlyProductSetInMiniCart, cartLocalStorageJSON])

  const addedProductBadges = useMemo(
    () =>
      (resolvedProductEntry?.product?.productAvailableBadges
        ?.productAvailableBadge?.length ?? 0) > 0 &&
      (
        resolvedProductEntry?.product?.productAvailableBadges
          ?.productAvailableBadge as unknown as Array<{
          badge: { name: string; color: { hexCode?: string } }
        }>
      )?.map((badgeProps) => ({
        label: badgeProps.badge.name,
        background: badgeProps?.badge?.color?.hexCode
      })),
    [
      resolvedProductEntry?.product?.productAvailableBadges
        ?.productAvailableBadge
    ]
  )

  const priceInfoProps = useMemo(
    () =>
      getPriceInfoMiniCart(
        resolvedProductEntry?.totalWithoutDiscounts?.formattedValue,
        resolvedProductEntry?.totalPrice?.formattedValue,
        showOldPrices
      ),
    [
      resolvedProductEntry?.totalPrice?.formattedValue,
      resolvedProductEntry?.totalWithoutDiscounts?.formattedValue,
      showOldPrices
    ]
  )

  const configPopoverMiniCart = useMemo(
    () => ({
      open: activeMiniCart,
      verticalPositionAnchor: PopoverVerticalPositionProps.Top,
      horizontalPositionAnchor: PopoverHorizontalPositionProps.Right,
      verticalTransform: PopoverVerticalPositionProps.Top,
      horizontalTransform: PopoverHorizontalPositionProps.Center
    }),
    [activeMiniCart]
  )

  const productImageProps = {
    height: '100',
    width: '100',
    src: `${getAssetImageUrl(variantAssetsByAddToCart?.[0]) ?? ''}?width=100`
  }

  const childProductInfoProps = useMemo(() => {
    if (!childProductEntries?.length || !variants?.length) {
      return undefined
    }
    return childProductEntries.map((childEntry) => {
      const childCode = childEntry?.product?.code
      const childVariant = childCode
        ? variants.find((v) => v.globalArticleCode === childCode)
        : undefined
      const childAssetUrl = getAssetImageUrl(childVariant?.assets?.[0])
      return {
        productName: childEntry?.product?.name,
        productQuantity: childEntry?.quantity,
        productQuantityLabel: shopConfig?.quantityLabel,
        priceInfoProps: getPriceInfoMiniCart(
          childEntry?.totalWithoutDiscounts?.formattedValue,
          childEntry?.totalPrice?.formattedValue,
          showOldPrices
        ),
        ...(childAssetUrl
          ? {
              productImageProps: {
                height: '100',
                width: '100',
                src: `${childAssetUrl}?width=100`
              }
            }
          : {})
      }
    })
  }, [childProductEntries, variants, shopConfig?.quantityLabel, showOldPrices])

  const configMiniCart = {
    popoverProps: configPopoverMiniCart,
    title: shopConfig?.successMessage,
    onClose: () => setActiveMiniCart(false),
    ...(addedProductBadges && {
      productChipsProps: { chipPropsList: addedProductBadges }
    }),
    priceInfoProps,
    ...(variantAssetsByAddToCart?.length
      ? {
          productImageProps
        }
      : {}),
    productName: resolvedProductEntry?.product?.name,
    productVariantName: (
      resolvedProductEntry?.product as { nuance?: { description?: string } }
    )?.nuance?.description,
    productQuantity: resolvedProductEntry?.quantity,
    productQuantityLabel: shopConfig?.quantityLabel,
    actions: [
      {
        label: shopConfig?.checkoutCTALabel,
        linkUrl: shopConfig?.checkoutCTAUrl,
        onClick: pushDataLayerMiniCartValidateOrderClick
      },
      {
        label: `${shopConfig?.cartCTALabel} (${miniCartTotalUnitCount ?? 0})`,
        linkUrl: shopConfig?.cartCTAUrl,
        onClick: pushDataLayerMiniCartSeeCartClick
      }
    ],
    idleCloseTimeout: 5000,
    blurCloseTimeout: 3000,
    'aria-label': shopConfig?.closeButtonAriaLabel,
    ...(childProductInfoProps ? { childProductInfoProps } : {})
  }

  return {
    activeMiniCart,
    setActiveMiniCart,
    configMiniCart
  }
}
